import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";
import { Pagination } from "@/domain/entities/Pagination";
import {
  Invoice,
  ListInvoiceItem,
  PrintReceiptListInvoice,
  SendInvoiceResponseData,
  AddDraftResponseData,
  GetPreviewData,
  GetPreviewResponseData,
  InvoiceForUpdate,
  InvoiceDetail,
  SttInvoiceForUpdate,
  SuccessUpdateReceipt
} from "@/domain/entities/PrintReceipt";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import { AxiosResponse } from "axios";

export class PrintReceiptMapper {
  public convertListInvoices(result: AxiosResponse): PrintReceiptListInvoice {
    const { data, meta } = result.data;

    return new PrintReceiptListInvoice({
      data: data
        ? data.map((item: any) => {
            return new ListInvoiceItem({
              id: item.id,
              invoiceNo: item.invoice_no,
              billedToName: item.billed_to_name,
              billedToPhone: item.billed_to_phone,
              totalAmount: item.total_amount,
              totalPaid: item.total_paid,
              status: item.status,
              isInvoicePrinted: item.is_invoice_printed,
              isInvoiceSent: item.is_invoice_sent,
              createdAt: item.created_at,
              createdByName: item.created_by_name,
              updatedAt: item.updated_at,
              updatedByName: item.updated_by_name
            });
          })
        : [],
      pagination: new Pagination(meta.page, meta.limit, meta.total_records)
    });
  }
  public convertCreateInvoiceData(
    result: AxiosResponse<any>
  ): ResponsePayloadV2 {
    const { data } = result;

    return new ResponsePayloadV2(convertSnakeToCamel(data));
  }

  public convertDetailInvoiceData(result: AxiosResponse<any>): Invoice {
    const {
      data: { data }
    } = result;
    return new Invoice(convertSnakeToCamel(data));
  }

  public convertBookingHistoryDetailDataFromApi(
    result: AxiosResponse<any>
  ): ResponsePayloadV2 {
    const {
      data: { data }
    } = result;
    const converted = convertSnakeToCamel(data);
    return new ResponsePayloadV2({
      ...converted,
      data: converted?.listStt
    });
  }

  public convertInvoiceFromApi(
    res: AxiosResponse<any>
  ): SendInvoiceResponseData {
    const { data } = res;

    return new SendInvoiceResponseData({
      invoiceId: data?.data?.invoice_id,
      invoiceNo: data?.data?.invoice_no,
      sentCount: data?.data?.sent_count,
      deliveries: data?.data?.deliveries
    });
  }

  public convertDraftFromApi(res: AxiosResponse<any>): AddDraftResponseData {
    const { data } = res;

    return new AddDraftResponseData({
      totalStt: data.total_stt,
      totalSenders: data.total_senders
    });
  }

  public convertPreviewFromApi(
    res: AxiosResponse<any>
  ): GetPreviewResponseData {
    const { data } = res;

    const mappedData = (data.data || []).map((item: any) => {
      return new GetPreviewData({
        id: item.id,
        tariff: item.tariff,
        sttNo: item.stt_no ?? "",
        shipmentId: item.shipment_id ?? "",
        isCod: item.is_cod ?? false,
        isDfod: item.is_dfod ?? false,
        isCashless: item.is_cashless ?? false,
        createdAt: item.created_at ?? "",
        recipientName: item.recipient_name ?? "",
        recipientPhoneMasked: item.recipient_phone_masked ?? "",
        destination3lc: item.destination_3lc ?? "",
        destinationName: item.destination_name ?? "",
        productType: item.product_type ?? "",
        chargeableWeight: item.chargeable_weight ?? 0,
        amount: item.amount ?? 0,
        paymentLabel: item.payment_label ?? "",
        senderName: item.sender_name ?? "",
        senderPhone: item.sender_phone ?? ""
      });
    });

    return new GetPreviewResponseData({
      data: mappedData
    });
  }

  public convertDetailInvoiceForUpdateFromApi(
    result: AxiosResponse<any>
  ): InvoiceForUpdate {
    const {
      data: { data }
    } = result;
    return new InvoiceForUpdate({
      header: new InvoiceDetail({
        invoiceId: data.header.invoice_id,
        invoiceNo: data.header.invoice_no,
        referenceType: data.header.reference_type,
        referenceName: data.header.reference_name,
        billedToName: data.header.billed_to_name,
        billedToPhone: data.header.billed_to_phone,
        createdAt: data.header.created_at,
        totalStt: data.header.total_stt,
        totalTariff: data.header.total_tariff,
        totalAmount: data.header.total_amount,
        totalPaid: data.header.total_paid,
        balanceDue: data.header.balance_due,
        status: data.header.status,
        note: data.header.note,
        billedToSend: data.header?.billed_to_send?.length > 0 
          ? data.header?.billed_to_send 
          : []
      }),
      items: data.items.length
        ? data.items.map((item: any) => {
            return new SttInvoiceForUpdate({
              no: item.no,
              sttNo: item.stt_no,
              shipmentId: item.shipment_id,
              isCod: item.is_cod,
              isDfod: item.is_dfod,
              isCashless: item.is_cashless,
              createdAt: item.created_at,
              recipientName: item.recipient_name,
              recipientPhoneMasked: item.recipient_phone_masked,
              senderName: item.sender_name,
              senderPhone: item.sender_phone,
              destinationCity: item.destination_city,
              productType: item.product_type,
              chargeableWeight: item.chargeable_weight,
              tariff: item.tariff,
              amount: item.amount,
              paymentLabel: item.payment_label
            });
          })
        : []
    });
  }

  public convertDraftPreviewForUpdateFromApi(
    result: AxiosResponse<any>
  ): InvoiceForUpdate {
    const {
      data: { data }
    } = result;
    return new InvoiceForUpdate({
      header: new InvoiceDetail({
        totalStt: data.header.total_stt,
        totalTariff: data.header.total_tariff,
        totalAmount: data.header.total_amount
      }),
      items: data.items.length
        ? data.items.map((item: any) => {
            return new SttInvoiceForUpdate({
              no: item.no,
              sttNo: item.stt_no,
              shipmentId: item.shipment_id,
              isCod: item.is_cod,
              isDfod: item.is_dfod,
              isCashless: item.is_cashless,
              createdAt: item.created_at,
              recipientName: item.recipient_name,
              recipientPhoneMasked: item.recipient_phone_masked,
              senderName: item.sender_name,
              senderPhone: item.sender_phone,
              destinationCity: item.destination_city,
              productType: item.product_type,
              chargeableWeight: item.chargeable_weight,
              tariff: item.tariff,
              amount: item.amount,
              paymentLabel: item.payment_label
            });
          })
        : []
    });
  }

  public convertUpdateReceiptFromApi(
    result: AxiosResponse<any>
  ): SuccessUpdateReceipt {
    const {
      data: { data }
    } = result;
    return new SuccessUpdateReceipt({
      invoiceId: data.id,
      invoiceNo: data.invoice_no,
      billedToName: data.billed_to_name,
      billedToPhone: data.billed_to_phone,
      totalAmount: data.total_amount,
      totalPaid: data.total_paid,
      status: data.status,
      note: data.note,
      updatedAt: data.updated_at
    });
  }
}
